<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { customValidators } from "@/i18n/utils/i18n-validators";
import type { CmsElementForm } from "@shopware-pwa/composables-next";
import { useCmsTranslations } from "@shopware-pwa/composables-next";
import { ApiClientError } from "@shopware/api-client";
import type { ApiError } from "@shopware/api-client";
import {
  useCmsElementConfig,
  useNavigationContext,
  useSalutations,
  useShopwareContext,
} from "#imports";
import { computed, reactive, ref } from "vue";
import { defu } from "defu";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  content: CmsElementForm;
}>();

type Translations = {
  form: {
    salutation: string;
    salutationPlaceholder: string;
    firstName: string;
    firstNamePlaceholder: string;
    lastName: string;
    lastNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    subject: string;
    subjectPlaceholder: string;
    comment: string;
    commentPlaceholder: string;
    privacy: string;
    dataProtection: string;
    submit: string;
    messages: {
      contactFormSuccess: string;
    };
  };
};

let translations: Translations = {
  form: {
    salutation: "Salutation",
    salutationPlaceholder: "Enter salutation...",
    firstName: "First name",
    firstNamePlaceholder: "Enter first name...",
    lastName: "Last name",
    lastNamePlaceholder: "Enter last name...",
    email: "Email address",
    emailPlaceholder: "Enter email address...",
    phone: "Phone number",
    phonePlaceholder: "Enter phone number...",
    subject: "Subject",
    subjectPlaceholder: "Enter subject...",
    comment: "Comment",
    commentPlaceholder: "Enter comment...",
    privacy: "Privacy",
    dataProtection: "I have read the data protection information.",
    submit: "Submit",
    messages: {
      contactFormSuccess:
        "We have received your contact request and will process it as soon as possible.",
    },
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const loading = ref<boolean>();
const formSent = ref<boolean>(false);
const { t } = useI18n();
type ErrorMessages = ApiClientError<{
  errors: ApiError[];
}>;

const errorMessages = ref<ErrorMessages[]>([]);

const { required, minLength, email } = customValidators();
const { getSalutations } = useSalutations();
const { foreignKey } = useNavigationContext();
const { apiClient } = useShopwareContext();
const { getConfigValue } = useCmsElementConfig(props.content);

const getConfirmationText = computed(
  () =>
    getConfigValue("confirmationText") ??
    translations.form.messages.contactFormSuccess
);
const getFormTitle = computed(() => getConfigValue("title") || "Contact");
const state = reactive({
  salutationId: "",
  firstName: "",
  lastName: "",
  email: "",
  subject: t("form.subjectContent"),
  comment: t("form.commentContent"),
  phone: "",
  checkbox: true,
  companyName: "", // New field for company name
  businessType: "", // New field for business type
  address: "", // New field for address
  country: "", // New field for country
  city: "", // New field for city
  postcode: "", // New field for postal code
});

const rules = computed(() => ({
  email: {
    required,
    email,
  },
  firstName: {
    required,
    minLength: minLength(3),
  },
  lastName: {
    required,
    minLength: minLength(3),
  },
  salutationId: {
    required,
  },
  phone: {
    required,
    minLength: minLength(3),
  },
  subject: {
    required,
    minLength: minLength(3),
  },
  comment: {
    required,
    minLength: minLength(10),
  },
  checkbox: {
    required,
    isTrue: (value: boolean) => value === true,
  },
  address: {
    required,
    minLength: minLength(5),
  },
  country: {
    required,
  },
  city: {
    required,
    minLength: minLength(2),
  },
  postcode: {
    required,
    minLength: minLength(4),
  },
}));

const isSubmited = ref<boolean>(false);
const $v = useVuelidate(rules, state);
const invokeSubmit = async () => {
  $v.value.$touch();
  const valid = await $v.value.$validate();
  if (valid) {
    isSubmited.value = true;
    loading.value = true;
    try {
      await apiClient.invoke("sendContactMail post /contact-form", {
        body: {
          ...state,
          navigationId: foreignKey.value,
        },
      });
      formSent.value = true;
    } catch (e) {
      if (e instanceof ApiClientError) {
        errorMessages.value = e.details.errors;
      }
    } finally {
      loading.value = false;
    }
  }
};
</script>
<template>
  <form class="max-w-4xl mx-auto custom-form" @submit.prevent="invokeSubmit">
    <template v-if="!formSent">
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 mb-4 lg:mb-12 gap-y-4 gap-x-2 md:gap-x-7"
      >
        <div class="relative">
          <input
            id="company-name"
            v-model="state.companyName"
            name="company-name"
            type="text"
            autocomplete="company-name"
            class="input-field input-sty peer placeholder-transparent focus:border-2 focus:border-dark-bg"
            :class="[
              $v.companyName?.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.companyName?.$touch()"
          />
          <label
            for="company-name"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.companyName || $v.companyName?.$error,
              'text-red-500': $v.companyName?.$error, 
              'text-dark-bg': !$v.companyName?.$error 
            }"
            >{{ $t("form.companyName") }}</label
          >
          <span v-if="$v.companyName?.$error" class="form-error text-xs">
            {{ $v.companyName?.$errors[0]?.$message }}
          </span>
        </div>

        <div class="relative">
          <input
            id="business-type"
            v-model="state.businessType"
            name="business-type"
            type="text"
            autocomplete="business-type"
            class="input-field input-sty peer placeholder-transparent focus:border-2 focus:border-dark-bg"
            :class="[
              $v.businessType?.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.businessType?.$touch()"
          />
          <label
            for="business-type"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.businessType || $v.businessType?.$error,
              'text-red-500': $v.businessType?.$error, 
              'text-dark-bg': !$v.businessType?.$error 
            }"
            >{{ $t("form.businessType") }}</label
          >
          <span v-if="$v.businessType?.$error" class="form-error text-xs">
            {{ $v.businessType?.$errors[0]?.$message }}
          </span>
        </div>
      </div>

      <div
        class="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 mb-4 lg:mb-12 gap-y-4 gap-x-2 md:gap-x-7"
      >
        <div class="relative">
          <select
            id="salutation"
            v-model="state.salutationId"
            name="salutation"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.salutationId.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.salutationId.$touch()"
          >
            <option
              v-for="salutation in getSalutations"
              :key="salutation.id"
              :value="salutation.id"
            >
              {{ salutation.displayName }}
            </option>
          </select>
          <label for="salutation" class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.salutationId || $v.salutationId.$error,
              'text-red-500': $v.salutationId.$error, 
              'text-dark-bg': !$v.salutationId.$error 
            }">
            {{ $t('form.salutation') }} *
          </label>
          <span v-if="$v.salutationId.$error" class="form-error text-xs">
            {{ $v.salutationId.$errors[0].$message }}
          </span>
        </div>
        <div class="relative">
          <input
            id="first-name"
            v-model="state.firstName"
            name="first-name"
            type="text"
            autocomplete="first-name"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.firstName.$error
                ? 'border-red-600 focus:border-red-600 border-2 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.firstName.$touch()"
          />
          <label for="first-name" class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.firstName || $v.firstName.$error,
              'text-red-500': $v.firstName.$error, 
              'text-dark-bg': !$v.firstName.$error 
            }">
            {{ $t('form.firstName') }} *
          </label>
          <span v-if="$v.firstName.$error" class="form-error text-xs">
            {{ $v.firstName.$errors[0].$message }}
          </span>
        </div>
        <div class="relative">
          <input
            id="last-name"
            v-model="state.lastName"
            name="last-name"
            type="text"
            autocomplete="last-name"
            class="input-field input-sty peer placeholder-transparent focus:border-2 focus:border-dark-bg"
            :class="[
              $v.lastName.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.lastName.$touch()"
          />
          <label for="last-name" class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.lastName || $v.lastName.$error,
              'text-red-500': $v.lastName.$error, 
              'text-dark-bg': !$v.lastName.$error 
            }">
            {{ $t('form.lastName') }} *
          </label>
          <span v-if="$v.lastName.$error" class="form-error text-xs">
            {{ $v.lastName.$errors[0].$message }}
          </span>
        </div>
      </div>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 mb-4 lg:mb-12 gap-y-4 gap-x-2 md:gap-x-7"
      >
        <div class="relative">
          <input
            id="email-address"
            v-model="state.email"
            name="email"
            type="email"
            autocomplete="email"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.email.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.email.$touch()"
          />
          <label for="email-address" class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.email || $v.email.$error,
              'text-red-500': $v.email.$error, 
              'text-dark-bg': !$v.email.$error 
            }">
            {{ $t('form.email') }} *
          </label>
        
          <span v-if="$v.email.$error" class="form-error text-xs">
            {{ $v.email.$errors[0].$message }}
          </span>
        </div>
        <div class="relative">
          <input
            id="phone"
            v-model="state.phone"
            name="phone"
            type="text"
            autocomplete="phone"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.phone.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.phone.$touch()"
          />
          <label for="phone" class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.phone || $v.phone.$error,
              'text-red-500': $v.phone.$error, 
              'text-dark-bg': !$v.phone.$error 
            }">
            {{ $t('form.phone') }} *
          </label>
          
          <span v-if="$v.phone.$error" class="form-error text-xs">
            {{ $v.phone.$errors[0].$message }}
          </span>
        </div>
      </div>
      <div class="grid w-full mb-4 lg:mb-12">
        <div class="relative">
          <input
            id="address"
            v-model="state.address"
            name="address"
            type="text"
            autocomplete="address"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.address.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.address.$touch()"
          />
          <label
            for="address"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-2 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.address || $v.address?.$error,
              'text-red-500': $v.address?.$error, 
              'text-dark-bg': !$v.address?.$error 
            }"
            >{{ $t("form.address") }} *</label
          >
          <span v-if="$v.address.$error" class="form-error text-xs">
            {{ $v.address.$errors[0].$message }}
          </span>
        </div>
      </div>
      <div
        class="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 mb-4 lg:mb-12 gap-y-4 gap-x-2 md:gap-x-7"
      >
        <!-- Country -->
        <div class="relative">
          <input
            id="country"
            v-model="state.country"
            name="country"
            type="text"
            autocomplete="country"
            class="input-field input-sty peer placeholder-transparent focus:border-2 focus:border-dark-bg"
            :class="[
              $v.country?.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.country?.$touch()"
          />
          <label
            for="country"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.country || $v.country?.$error,
              'text-red-500': $v.country?.$error, 
              'text-dark-bg': !$v.country?.$error 
            }"
            >{{ $t("form.country") }}</label
          >
          <span v-if="$v.country?.$error" class="form-error text-xs">
            {{ $v.country?.$errors[0]?.$message }}
          </span>
        </div>

        <!-- City -->
        <div class="relative">
          <input
            id="city"
            v-model="state.city"
            name="city"
            type="text"
            autocomplete="city"
            class="input-field input-sty peer placeholder-transparent focus:border-2 focus:border-dark-bg"
            :class="[
              $v.city?.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.city?.$touch()"
          />
          <label
            for="city"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.city || $v.city?.$error,
              'text-red-500': $v.city?.$error, 
              'text-dark-bg': !$v.city?.$error 
            }"
            >{{ $t("form.city") }}</label
          >
          <span v-if="$v.city?.$error" class="form-error text-xs">
            {{ $v.city?.$errors[0]?.$message }}
          </span>
        </div>

        <!-- postcode -->
        <div class="relative">
          <input
            id="postcode"
            v-model="state.postcode"
            name="postcode"
            type="text"
            autocomplete="postcode"
            class="input-field input-sty peer placeholder-transparent focus:border-2 focus:border-dark-bg"
            :class="[
              $v.postcode?.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.postcode?.$touch()"
          />
          <label
            for="postcode"
            class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.postcode || $v.postcode?.$error,
              'text-red-500': $v.postcode?.$error, 
              'text-dark-bg': !$v.postcode?.$error 
            }"
            >{{ $t("form.postcode") }}</label
          >
          <span v-if="$v.postcode?.$error" class="form-error text-xs">
            {{ $v.postcode?.$errors[0]?.$message }}
          </span>
        </div>
      </div>

      <!-- ------hidden required fields-->
      <div class="grid w-full mb-4 lg:mb-12 hidden">
        <div class="relative">
          <input
            id="subject"
            v-model="state.subject"
            name="subject"
            type="text"
            autocomplete="subject"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.subject.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            @blur="$v.subject.$touch()"
          />
          <label for="subject" class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.subject || $v.subject.$error,
              'text-red-500': $v.subject.$error, 
              'text-dark-bg': !$v.subject.$error 
            }">
            {{ $t('form.subject') }} *
          </label>

          <span v-if="$v.subject.$error" class="form-error text-xs">
            {{ $v.subject.$errors[0].$message }}
          </span>
        </div>
      </div>
      <div class="grid w-full mb-4 lg:mb-12 hidden">
        <div class="relative">
          <textarea
            id="comment"
            v-model="state.comment"
            name="comment"
            type="text"
            autocomplete="comment"
            class="input-field input-sty peer placeholder-transparent"
            :class="[
              $v.comment.$error
                ? 'border-red-600 focus:border-red-600 border-2'
                : 'border-gray-300 focus:border-gray-500',
            ]"
            rows="5"
            @blur="$v.comment.$touch()"
          />
          <label for="comment" class="input-label top-3 label-sty peer-focus:transform peer-focus:-translate-y-5 peer-focus:text-xs peer-focus:text-dark-bg peer-focus:px-2"
            :class="{ 
              'transform -translate-y-5 text-xs px-2': state.comment || $v.comment.$error,
              'text-red-500': $v.comment.$error, 
              'text-dark-bg': !$v.comment.$error 
            }">
            {{ $t('form.comment') }} *
          </label>
          <span
            v-if="$v.comment.$error"
            class="form-error text-xs"
          >
            {{ $v.comment.$errors[0].$message }}
          </span>
        </div>
      </div>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 mb-4 lg:mb-12 gap-y-4 gap-x-2 md:gap-x-7 hidden"
      >
        <div class="col-span-12">
          <label>{{ $t("form.privacy") }} *</label>
          <div class="flex gap-3 items-start">
            <input
              id="privacy"
              v-model="state.checkbox"
              name="privacy"
              type="checkbox"
              class="mt-1 peer relative h-5 w-5 cursor-pointer appearance-none rounded border border-custom-grey shadow hover:shadow-md transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-dark-bg before:opacity-0 before:transition-opacity checked:border-dark-bg checked:bg-dark-bg checked:before:bg-slate-400 hover:before:opacity-10"
              :class="[
                $v.checkbox.$error 
                  ? 'border-red-600 focus:border-red-600 border-2'
                  : 'border-gray-300 focus:border-gray-500',
              ]"
            />
            <div>
              <label
                class="text-sm"
                :class="[
                  $v.checkbox.$error
                    ? 'text-red-600 focus:text-red-600'
                    : 'text-gray-300 focus:text-gray-500',
                ]"
                for="privacy"
              >
                {{ $t("checkout.termsAdnConditionsLabel") }}
              </label>
            </div>
          </div>
        </div>
      </div>
      <!-- ------hidden required fields end -->

      <div class="text-center flex justify-center">
        <button type="submit" class="btn-default btn-black-bg btn-w-icon"
          :disabled="isSubmited"
          :class="{ 'opacity-50 cursor-not-allowed': isSubmited }"
        >
          {{ $t('form.submit') }}
          <span class="ml-3"
            v-if="!isSubmited"
          >
            <i class="fa-solid fa-angle-right"></i>
          </span>
          <span
            class="ml-3"
            v-else
          >
            <i class="fa-solid fa-spinner animate-spin"></i>
          </span>
        </button>
      </div>
    </template>
    <template v-else>
      <p class="py-10 text-lg text-center">{{ getConfirmationText }}</p>
    </template>
  </form>
</template>
